/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeftCircle, ArrowRightCircle } from 'lucide-react';

// Minimal CSS kept inline to preserve 3D layout
const EMBEDDED_CSS = `
.cascade-slider_container {
  position: relative;
  width: 100%;
  margin: 0 auto;
  z-index: 20;
  user-select: none;
  -webkit-user-select: none;
  touch-action: pan-y;
}
.cascade-slider_slides { position: relative; height: 100%; }
.cascade-slider_item {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.3);
  transition: all 0.9s ease;
  opacity: 0;
  z-index: 1;
  cursor: grab;
}
.cascade-slider_item.now { cursor: default; }
.cascade-slider_item:active { cursor: grabbing; }
.cascade-slider_item.next { left: 50%; transform: translate(-120%, -50%) scale(0.6); opacity: 1; z-index: 4; }
.cascade-slider_item.prev { left: 50%; transform: translate(20%, -50%) scale(0.6); opacity: 1; z-index: 4; }
.cascade-slider_item.now  { transform: translate(-50%, -50%) scale(1); opacity: 1; z-index: 5; }
/* arrows */
.cascade-slider_arrow {
  display: flex; align-items: center; justify-content: center;
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 40px; height: 40px; cursor: pointer; z-index: 6;
  transition: all 0.3s ease;
}
@media (max-width: 575px) { .cascade-slider_arrow-left { left: 15px; } .cascade-slider_arrow-right { right: 15px; } }
@media (min-width: 576px) { .cascade-slider_arrow-left { left: 20px; } .cascade-slider_arrow-right { right: 20px; } }
.cascade-slider_slides img {
  max-width: 150px; height: auto; border-radius: 20px; display: block; transition: filter 0.9s ease;
}
.cascade-slider_item:not(.now) img { filter: grayscale(0.9); }
@media (min-width: 414px) { .cascade-slider_container { height: 220px; } .cascade-slider_slides img { max-width: 200px; } }
@media (min-width: 576px) { .cascade-slider_container { height: 300px; } .cascade-slider_slides img { max-width: 240px; } }
@media (min-width: 768px) { .cascade-slider_item.next { transform: translate(-125%, -50%) scale(0.6); } .cascade-slider_item.prev { transform: translate(25%, -50%) scale(0.6); } .cascade-slider_slides img { max-width: 260px; } }
@media (min-width: 991px) {
  .cascade-slider_item.next { transform: translate(-115%, -50%) scale(0.55); z-index:4; }
  .cascade-slider_item.prev { transform: translate(15%, -50%) scale(0.55); z-index:4; }
  .cascade-slider_item.next2 { transform: translate(-150%, -50%) scale(0.37); z-index:1; }
  .cascade-slider_item.prev2 { transform: translate(50%, -50%) scale(0.37); z-index:2; }
  .cascade-slider_slides img { max-width: 300px; }
  .cascade-slider_container { height: 320px; }
}
@media (min-width: 1100px) {
  .cascade-slider_item.next { transform: translate(-130%, -50%) scale(0.55); }
  .cascade-slider_item.prev { transform: translate(30%, -50%) scale(0.55); }
  .cascade-slider_item.next2 { transform: translate(-180%, -50%) scale(0.37); }
  .cascade-slider_item.prev2 { transform: translate(80%, -50%) scale(0.37); }
  .cascade-slider_slides img { max-width: 340px; }
}
/* Mobile tightening to keep items centered */
@media (max-width: 575px) {
  .cascade-slider_container { height: 220px; }
  .cascade-slider_item.next { transform: translate(-95%, -50%) scale(0.6); }
  .cascade-slider_item.prev { transform: translate(-5%, -50%) scale(0.6); }
}
`;

const getSlideClasses = (index, activeIndex, total, visibleCount) => {
  const diff = index - activeIndex;
  if (diff === 0) return 'now';
  if (diff === 1 || diff === -total + 1) return 'next';
  if (visibleCount === 5 && (diff === 2 || diff === -total + 2)) return 'next2';
  if (diff === -1 || diff === total - 1) return 'prev';
  if (visibleCount === 5 && (diff === -2 || diff === total - 2)) return 'prev2';
  return '';
};

const ThreeDImageCarousel = ({
  slides,
  itemCount = 5,
  autoplay = false,
  delay = 3,
  pauseOnHover = true,
  className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const autoplayIntervalRef = useRef(null);
  const total = slides.length;

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const swipeThreshold = 50;

  const navigate = useCallback((direction) => {
    setActiveIndex((current) => {
      if (direction === 'next') return (current + 1) % total;
      return (current - 1 + total) % total;
    });
  }, [total]);

  const startAutoplay = useCallback(() => {
    if (autoplay && total > 1) {
      if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
      autoplayIntervalRef.current = window.setInterval(() => navigate('next'), delay * 1000);
    }
  }, [autoplay, delay, navigate, total]);

  const stopAutoplay = useCallback(() => {
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
      autoplayIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay]);

  const handleMouseEnter = () => { if (autoplay && pauseOnHover) stopAutoplay(); };
  const handleExit = (e) => {
    if (autoplay && pauseOnHover) startAutoplay();
    if (isDragging) handleEnd(e.clientX);
  };

  const handleStart = (clientX) => { setIsDragging(true); setStartX(clientX); stopAutoplay(); };
  const handleEnd = (clientX) => {
    if (!isDragging) return;
    const distance = clientX - startX;
    if (Math.abs(distance) > swipeThreshold) navigate(distance < 0 ? 'next' : 'prev');
    setIsDragging(false); setStartX(0);
  };

  const onMouseDown = (e) => handleStart(e.clientX);
  const onMouseUp = (e) => { handleEnd(e.clientX); startAutoplay(); };
  const onTouchStart = (e) => handleStart(e.touches[0].clientX);
  const onTouchEnd = (e) => { handleEnd(e.changedTouches[0].clientX); startAutoplay(); };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: EMBEDDED_CSS }} />
      <div
        className={`cascade-slider_container ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleExit}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="cascade-slider_slides">
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className={`cascade-slider_item ${getSlideClasses(index, activeIndex, total, itemCount)}`}
              data-slide-number={index}
            >
              <a href={slide.href || '#'} aria-label={slide.alt || `slide-${index + 1}`}>
                <img
                  src={slide.src}
                  alt={slide.alt || `Slide ${index + 1}`}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `https://placehold.co/350x200/4F46E5/ffffff?text=Slide%20${index + 1}`;
                  }}
                />
              </a>
            </div>
          ))}
        </div>

        {total > 1 && (
          <>
            <span
              className="cascade-slider_arrow cascade-slider_arrow-left rounded-full shadow-lg bg-white/90 text-black p-2 hover:bg-white transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ border: '1px solid #E5E7EB' }}
              onClick={(e) => { e.stopPropagation(); navigate('prev'); }}
              data-action="prev"
            >
              <ArrowLeftCircle size={28} />
            </span>
            <span
              className="cascade-slider_arrow cascade-slider_arrow-right rounded-full shadow-lg bg-white/90 text-black p-2 hover:bg-white transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ border: '1px solid #E5E7EB' }}
              onClick={(e) => { e.stopPropagation(); navigate('next'); }}
              data-action="next"
            >
              <ArrowRightCircle size={28} />
            </span>
          </>
        )}
      </div>
    </>
  );
};

export default ThreeDImageCarousel;
